import * as React from 'react';
import { Navbar as NavbarBase, Group, Text, Code, ScrollArea, createStyles, ActionIcon } from '@mantine/core';
import { openConfirmModal, closeModal } from '@mantine/modals';
import { IconMenu } from '@tabler/icons-react';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';

// Assets
import HeaderLogo from '../../assets/CIC-logo-RGB-400-300x161.png';

// Components
import { LanguageSelectClient } from '../LanguageSelectClient';
import { GetHelpButton } from '../GetHelpButton';
import { OrganizationWidget } from '../OrganizationWidget';
import { UserWidget } from '../UserWidget';

// Local Components
import { LinksGroup } from './components/NavbarLinksGroup';

// Local
import pjson from '../../../package.json';

// Types
import { NavItem } from './types';

const { VITE_SENTRY_ENV } = import.meta.env;

const useStyles = createStyles((theme) => ({
    navbar: {
        // background: 'transparent',
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
        paddingBottom: 0
    },
    navbarRoot: {
        // borderColor: '#a0caef'
        height: '100vh',
        [theme.fn.smallerThan('sm')]: {
            top: 0,
            overflow: 'hidden',
            zIndex: 140,
            height: '3rem',
            bottom: 'auto',
            paddingTop: '0.5rem',
            borderBottom: '1px solid #dee2e6'
        }
    },
    navbarRootVisible: {
        // borderColor: '#a0caef'
        [theme.fn.smallerThan('sm')]: {
            height: '100vh',
            transition: 'height 0.2s ease'
        }
    },

    header: {
        padding: theme.spacing.sm,
        paddingTop: 0,
        marginLeft: `calc(${theme.spacing.md} * -1)`,
        marginRight: `calc(${theme.spacing.md} * -1)`,
        color: theme.colorScheme === 'dark' ? theme.white : theme.black,
        borderBottom: 'none'
    },

    links: {
        marginLeft: `calc(${theme.spacing.md} * -1)`,
        marginRight: `calc(${theme.spacing.md} * -1)`
    },

    linksInner: {
        paddingTop: theme.spacing.xl,
        paddingBottom: theme.spacing.xl
    },

    footer: {
        borderTop: '1px solid #dee2e6',
        marginLeft: `calc(${theme.spacing.md} * -1)`,
        marginRight: `calc(${theme.spacing.md} * -1)`
        // borderTop: 'none'
    },
    logo: {
        maxHeight: '30px',
        marginLeft: '8px'
    },
    isUATBannerShowing: {
        [theme.fn.smallerThan('sm')]: {
            marginTop: '1.6rem !important',
            paddingTop: '1.2rem',
            height: '3.8rem'
        },
        [theme.fn.largerThan('sm')]: {
            paddingTop: '4rem !important'
        }
    },
    isUATBannerShowingMenuHeight: {
        [theme.fn.smallerThan('sm')]: {
            height: 'calc(100vh - 1.5rem) !important'
        }
    }
}));
interface NavbarProps {
    items: NavItem[];
    orgWidget?: boolean;
    itemsOnly?: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({ items = [], orgWidget, itemsOnly, ...props }) => {
    const { cx, classes } = useStyles();
    const [menuOpened, { open: openMenu, close: closeMenu, toggle: toggleMenu }] = useDisclosure(false);
    const links = React.useMemo(() => items.map((item, index) => <LinksGroup {...item} key={index} />), [items]);
    const matches = useMediaQuery('(min-width: 768px)');
    const { t } = useTranslation();
    const openConfirmationModal = React.useCallback(() => {
        openConfirmModal({
            modalId: 'warnAboutMobileStyles',
            title: t('warnAboutMobileStyles.modal.title', { ns: 'components' }),
            children: (
                <>
                    <Text size="sm">{t('warnAboutMobileStyles.modal.body', { ns: 'components' })}</Text>
                </>
            ),
            labels: {
                confirm: t('ok', { ns: 'common' }),
                cancel: t('cancel', { ns: 'common' })
            },
            cancelProps: { style: { display: 'none' } },
            closeOnConfirm: true,
            onCancel: () => console.log('Cancel'),
            onConfirm: async () => {},
            lockScroll: false
        });
    }, []);

    React.useEffect(() => {
        if (matches === true && menuOpened) {
            closeMenu();
        }
        if (matches === false) {
            openConfirmationModal();
        } else {
            closeModal('warnAboutMobileStyles');
        }
    }, [matches]);

    return !itemsOnly ? (
        <NavbarBase
            width={{ sm: 250 }}
            p="md"
            className={cx(classes.navbar, {
                [classes.navbarRootVisible]: menuOpened
            })}
            classNames={{
                root: cx(classes.navbarRoot, {
                    [classes.isUATBannerShowing]: VITE_SENTRY_ENV !== 'prd',
                    [classes.isUATBannerShowingMenuHeight]: menuOpened && VITE_SENTRY_ENV !== 'prd'
                })
            }}
        >
            <NavbarBase.Section className={classes.header}>
                <Group position="apart" align="center">
                    <img data-cy="cic-header-logo" className={classes.logo} src={HeaderLogo} />
                    {!matches ? (
                        <Group>
                            <GetHelpButton subtle />
                            <ActionIcon variant="subtle" onClick={toggleMenu}>
                                <IconMenu size="1.125rem" />
                            </ActionIcon>
                        </Group>
                    ) : (
                        <>{VITE_SENTRY_ENV !== 'prd' ? <Code sx={{ fontWeight: 700 }}>v{pjson.version}</Code> : null}</>
                    )}
                </Group>
            </NavbarBase.Section>

            <NavbarBase.Section grow className={classes.links} component={ScrollArea}>
                <div className={classes.linksInner}>{links}</div>
            </NavbarBase.Section>

            <NavbarBase.Section className={classes.footer}>
                {orgWidget ? <OrganizationWidget /> : null}
                <Group py="xs" px="md">
                    <UserWidget />
                </Group>
                <Group py="xs" px="md">
                    <LanguageSelectClient />
                </Group>
            </NavbarBase.Section>
        </NavbarBase>
    ) : (
        <NavbarBase.Section grow className={classes.links} component={ScrollArea}>
            <div className={classes.linksInner}>{links}</div>
        </NavbarBase.Section>
    );
};
